import { normalizeConsolidatedPartnerships } from "../../../../components/ConsolidatedPartnerships/ConsolidatedPartnerships";
import { getGroupData, getPoaIndex } from "../../../../services/GroupContext";
import { processDateJS } from "../../../../utils/dateProcessor";
import { migrateUlt_parent_incorporation_jurisdiction } from "../../../../utils/migrationScripts";

export const getSummaryOfCalculationsData = () => {
  const savedData = getGroupData();
  const poaData = savedData?.periods_of_account?.[getPoaIndex()];

  const disallowed_amount = poaData?.group_disallowance ?? 0;
  const interest_restrictions = disallowed_amount > 0 ? "Yes" : "No";
  const reactivation_cap = poaData?.group_interest_reactivation_cap ?? 0;
  const actualreactivations = poaData?.group_reactivations ?? 0;
  const reactivations = actualreactivations > 0 ? "Yes" : "No";
  const aggr_ntie = poaData?.aggregate_net_tax_interest_expense ?? 0;
  const aggr_ntii = poaData?.aggregate_net_tax_interest_income ?? 0;
  const aggr_ebitda = poaData?.aggregate_tax_ebitda ?? 0;
  const int_all_bfwd = poaData?.group_unused_allowance ?? 0;
  const int_all_period = poaData?.group_interest_allowance ?? 0;
  const int_capacity_period = poaData?.group_interest_capacity ?? 0;
  const adj_ngie = poaData?.adjusted_net_group_interest_expense ?? 0;

  const group_ratio_election = poaData?.apply_group_ratio_election;
  const qual_ngie = group_ratio_election
    ? poaData?.group_ratio_blended_election
      ? poaData?.blended_net_group_interest_expense
      : (poaData?.qualifying_net_group_interest_expense ?? 0)
    : "";
  const group_ebitda = group_ratio_election
    ? poaData?.group_ebitda
      ? poaData?.group_ebitda
      : 0
    : "";
  const group_ratio = group_ratio_election
    ? poaData?.gr_percentage
      ? poaData?.gr_percentage
      : 0
    : "";
  const poaInvestors = poaData?.investor_group_data;
  const investorsArray = poaInvestors?.map(
    (investor) => investor["investor_group"],
  );
  const investorsList = investorsArray
    ? investorsArray.length > 1
      ? investorsArray.join(", ")
      : investorsArray[0]
    : "";

  return {
    interest_restrictions,
    disallowed_amount,
    reactivation_cap,
    actualreactivations,
    reactivations,
    aggr_ntie,
    aggr_ntii,
    aggr_ebitda,
    int_all_bfwd,
    int_all_period,
    int_capacity_period,
    adj_ngie,
    group_ratio_election,
    qual_ngie,
    group_ebitda,
    group_ratio,
    poaInvestors,
    investorsArray,
    investorsList,
  };
};

export const getExcelData = () => {
  const {
    interest_restrictions,
    disallowed_amount,
    reactivation_cap,
    reactivations,
    aggr_ntie,
    aggr_ntii,
    aggr_ebitda,
    int_all_bfwd,
    int_all_period,
    int_capacity_period,
    adj_ngie,
    qual_ngie,
    group_ebitda,
    group_ratio,
  } = getSummaryOfCalculationsData();

  const excelData = {
    compNames: ["Company name"],
    APSD: ["Accounting Period Start Date"],
    APED: ["Accounting Period End Date"],
    period_length: ["Accounting Period length"],
    date_joined: ["Date joined group"],
    date_lefted: ["Date left group"],
    regarded_period: ["Regarded period for CIR (days)"],
    disregarded_percent: ["Percentage of the period that is regarded"],

    currency: ["Currency"],
    fx: ["Exchange rate (1 GBP = X Currency)"],

    nltr_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    note_1: ["Less: Included in the above"],
    nltr_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    nltr_exclude_impairment_gains_losses: ["Exclude Impairment gains/(losses)"],
    nltr_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    nltr_adjustment_amortised_cost_basis: [
      "Adjustment for election for amortised cost basis for fair valued creditor relationships",
    ],
    nltr_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    nltr_exclude_defined_benefit_pension: [
      "Exclude defined benefit pension income/expense included on loan relationships statement",
    ],
    nltr_bank_expenses: [
      "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
    ],
    nltr_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    nltr_adjustment_lease_finance_cost: [
      "Adjustment for finance costs associated with leases",
    ],
    nltr_other_adjustments_1: ["Other Adjustments 1"],
    nltr_other_adjustments_2: ["Other Adjustments 2"],
    nltr_other_adjustments_3: ["Other Adjustments 3"],
    ntlr_subtotal: ["Non-trading loan relationships - Subtotal"],

    ntdc_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    ntdc_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    ntdc_exclude_impairment_gains_losses: ["Exclude Impairment gains/(losses)"],
    ntdc_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    ntdc_exclude_ordinary_trade: [
      "Exclude amounts on contracts arising in the ordinary course of trade",
    ],
    "ntdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)": [
      "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
    ],
    ntdc_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    ntdc_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    ntdc_adjustment_disregard_regulations: [
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    ],
    ntdc_other_adjustments_1: ["Other Adjustments 1"],
    ntdc_other_adjustments_2: ["Other Adjustments 2"],
    ntdc_other_adjustments_3: ["Other Adjustments 3"],
    ntdc_subtotal: ["Non-trading derivative contracts - subtotal"],

    tlr_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    note_1: ["Less: Included in the above"],
    tlr_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    tlr_exclude_impairment_gains_losses: ["Exclude Impairment gains/(losses)"],
    tlr_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    tlr_adjustment_amortised_cost_basis: [
      "Adjustment for election for amortised cost basis for fair valued creditor relationships",
    ],
    tlr_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    tlr_exclude_defined_benefit_pension: [
      "Exclude defined benefit pension income/expense included on loan relationships statement",
    ],
    tlr_bank_expenses: [
      "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
    ],
    tlr_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    tlr_adjustment_lease_finance_cost: [
      "Adjustment for finance costs associated with leases",
    ],
    tlr_other_adjustments_1: ["Other Adjustments 1"],
    tlr_other_adjustments_2: ["Other Adjustments 2"],
    tlr_other_adjustments_3: ["Other Adjustments 3"],
    tlr_subtotal: ["Trading loan relationships - Subtotal"],

    tdc_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    tdc_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    tdc_exclude_impairment_gains_losses: ["Exclude Impairment gains/(losses)"],
    tdc_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    tdc_exclude_ordinary_trade: [
      "Exclude amounts on contracts arising in the ordinary course of trade",
    ],
    "tdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)": [
      "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
    ],
    tdc_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    tdc_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    tdc_adjustment_disregard_regulations: [
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    ],
    tdc_other_adjustments_1: ["Other Adjustments 1"],
    tdc_other_adjustments_2: ["Other Adjustments 2"],
    tdc_other_adjustments_3: ["Other Adjustments 3"],
    tdc_subtotal: ["Trading derivative contracts - subtotal"],

    ofa_total_per_source_sheet_for_period: [
      "Total Per Source Sheet For Period",
    ],
    ofa_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    ofa_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    ofa_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    ofa_adjustment_finance_lease: [
      "Adjustment to include Expense (income) associated with finance leases",
    ],
    ofa_adjustment_debt_factoring: [
      "Adjustment to include Expense (income) associated with debt factoring transactions",
    ],
    ofa_adjustment_service_concession: [
      "Adjustment to include Expense (income) associated with service concession arrangement if and to the extent accounted for as a financial liability",
    ],
    ofa_other_adjustments_1: ["Other Adjustments 1"],
    ofa_other_adjustments_2: ["Other Adjustments 2"],
    ofa_other_adjustments_3: ["Other Adjustments 3"],
    ofa_subtotal: ["Other financing arrangements - subtotal"],

    net_tax_interest_totals: ["Total net tax-interest expense/(income)"],

    cte_condition_a: [
      "Adjusted corporation tax earnings - Condition A (TIOPA 2010, s406(3))",
    ],
    cte_condition_b: [
      "Adjusted corporation tax earnings - Condition B (TIOPA 2010, s406(4))",
    ],
    cte_restrictions_reactivations_included: [
      "CIR (Restrictions) and reactivations included in the above",
    ],
    tebitda_adjustments: ["Adjustments"],
    cte_subtotal: [
      "Corporation tax earnings (excluding restrictions and reactivations)",
    ],

    s407_tiopa_net_tax_interest_expense: [
      "Net tax-interest expense/(income) - s407(1)(a)",
    ],
    s407_tiopa_capital_allowance_charges: [
      "Capital allowances and charges - s407(1)(b)",
    ],
    s407_tiopa_relevant_intangible_debits_credits: [
      "Relevant intangible debits and credits - s407(1)(c)",
    ],
    s407_tiopa_adjustment_intangible_debit_credits: [
      "Adjustment to intangible debits and credits brought in automatically (e.g. disposals above original cost)",
    ],
    s407_tiopa_other_period_losses: [
      "Losses from other periods - s407(1)(d)(i) and (ii)",
    ],
    s407_tiopa_loan_relationship_deficts_other_periods: [
      "Loan relationship deficits from other periods - s407(1)(e)",
    ],
    s407_tiopa_expenses_management_other_periods: [
      "Expenses of management from other periods - s407(1)(f)",
    ],
    s407_tiopa_group_relief: ["Group relief - s407(1)(g)"],

    s407_relief_rd_expenditure_credit: ["R&D expenditure credit - s407(3)(a)"],
    s407_relief_rd_additional_relief: ["R&D additional relief - s407(3)(b)"],
    s407_relief_trading_loss: [
      "Deemed trading loss for pre-trading expenditure - s407(3)(c)",
    ],
    s407_relief_contaminated_land_relief: [
      "Contaminated land relief - s407(3)(d)",
    ],
    s407_relief_creative_industries: ["Creative industries - s407(3)(e)-(j)"],
    s407_relief_charitable_donation: [
      "Qualifying charitable donation (from current or earlier period) - s407(3)(k)",
    ],
    s407_relief_patent_box_profits: [
      "Profits from patent box chargeable at a lower rate - s407(3)(l)",
    ],
    s407_relief_dtr_adjustment_tax_rate_equal_uk: ["DTR adjustment - s409"],
    s407_relief_adjustment_qualifying_infrastructure: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    s407_relief_adjustment_gaap_items: ["Adjustment for GAAP transition items"],
    s407_relief_finance_lease_depreciation: [
      "Finance lease depreciation - s460(1)(d)",
    ],
    s407_relief_reversal_of_lease_depreciation: [
      "Reversal of right-of-use lease depreciation in respect of operating leases",
    ],
    s407_relief_long_funding_lease_deduction: [
      "Long funding lease deduction - s460(1)(a)",
    ],
    s407_relief_finance_lease_rentals_receivable: [
      "Capital element of finance lease rentals receivable - s460(1)(c)",
    ],
    s407_relief_long_funding_lessee: [
      "Long funding lessee - amount by which a deduction is reduced under section 379 of CTA 2010 - s460(1)(b)",
    ],
    s407_relief_adjustment_misc_losses: [
      "Adjustment for miscellaneous losses not included in Condition B above",
    ],
    s407_relief_group_relief: [
      "Group relief included above not covered by s407(1)(g)",
    ],
    s407_relief_adjustment_disregarded_regulations: [
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    ],
    s407_relief_other_adjustments_1: ["Other Adjustments 1"],
    s407_relief_other_adjustments_2: ["Other Adjustments 2"],
    s407_relief_other_adjustments_3: ["Other Adjustments 3"],
    tax_ebitda: ["Tax-EBITDA - Total for period"],
    tax_ebitda_gbp: ["Tax-EBITDA - Total for period (GBP)"],

    tax_ebitda_adjustments: [
      "Tax-EBITDA Adjustments (to apportionment calculation only)",
    ],

    // note_2: ["Reversal of right-of-use lease depreciation in respect of operating leases"],

    net_dtr_adjustment_gbp: [
      "Credit for foreign taxes under s18(2) TIOPA 2010 in GBP",
    ],
    net_dtr_adjustment_lc: [
      "Credit for foreign taxes under s18(2) TIOPA 2010 in Local Currency",
    ],
    corporation_tax_rate_percentage: ["Corporation Tax Rate"],
    ct_rate_override: ["CT Rate override"],
    s407_relief_dtr_adjustment_tax_rate_uk: ["DTR adjustment - s409"],

    non_consenting_company: ["Non-consenting company?"],
    trade_small_or_negligible: ["Trade ceased and/or small or negligible?"],
    trade_uncommercial_or_non_statutory: [
      "Trade uncommercial or non-statutory?",
    ],
    investment_business_ceased: ["Investment business ceased?"],
    cta10_change_in_ownership_rules: [
      "CTA10 Change in Company Ownership rules apply?",
    ],

    /*
        bfd_ntlr: ["BFD - Non-trading loan relationship amounts"],
        bfd_ntd: ["BFD - Non-trading derivative amounts"],
        bfd_tlr: ["BFD - Trading loan relationship amounts"],
        bfd_td: ["BFD - Trading derivatie amounts"],
        bfd_ofa: ["BFD - Other financing amounts"],
        bfd_total: ["BFD - Total"],
        */

    type_reit_company: ["REIT"],
    type_ring_fence_company: ["Ring fence company"],
    type_investment_manager: ["Investment company"],
    type_insurance_company: ["Insurance company"],
    type_shipping_company: ["Shipping company"],
    type_cooperative_company: ["Cooperative/community benefit society etc."],
    type_charity_company: ["Charity"],
    type_banking_company: ["Banking company"],

    // CIR Calculations
    aggregate_ebitda: ["Aggregate Tax EBITDA", "A"],
    fixed_ratio: ["Fixed ratio (30%)", "B = (A x 30%)"],
    angie: ["Adjusted Net Group Interest Expense (ANGIE)", "C"],
    excess_debt: ["Excess Debt cap brought forward", "D"],
    fixed_debt: ["Fixed Ratio Debt Cap (s400(1))", "E = (C + D)"],
    basic_interest: [
      "Basic interest allowance - lower of B and E (s397(1))",
      "F",
    ],
    antii: ["Aggregate Net Tax Interest Income (ANTII)", "G"],
    interest_allowance: ["Interest allowance (s396(1))", "H = (F + G)"],

    qngie: ["Qualifying Net Group Interest Expense (QNGIE)", "A"],
    group_ebitda: ["Group EBITDA", "B"],
    group_ratio: ["Group ratio % (s399)", "C = (A / B)"],

    aggregate_ebitda2: ["Aggregate Tax EBITDA", "D"],
    group_ratio_ebitda: ["Group ratio % of Tax EBITDA", "E = (D X C)"],

    excess_debt2: ["Excess Debt cap brought forward", "F"],
    group_debt_cap: ["Group ratio debt cap (s400(2))", "G = (A + F)"],

    group_basic_interest: [
      "Basic interest allowance - lower of E and G (s398(1))",
      "H",
    ],
    group_interest_allowance: ["Interest allowance (s396(1))", "H = (F + G)"],

    group_ratio_election: ["Group Ratio Election being made?"],
    computation_int_allowance: ["Interest allowance", "A"],
    unused_allowance: ["Unused interest allowance brought forward", "B"],
    de_minimis: ["De minimis (Note: Assumes no leap year)", "C"],
    interest_capacity: [
      "Interest capacity (Greater of interest allowance plus brought forward allowance and de minimis) (s392)",
      "D = Greater of (A + B) and C",
    ],
    aggregate_interest_expense: ["Aggregate Net Tax Interest Expense", "E"],
    disallowed_amount: ["Disallowed Amount (s373(1)-(2))", "F = (E - D)"],
    available_reactivation: [
      "Amount available for Reactivation (Group reactivation cap) (s373(3)-(4))",
      "G = (A - E)",
    ],

    // CIR Attributes
    current_int_allowance: ["Interest allowance for the current period", "A"],
    current_tax_interest_expense: [
      "Aggregate Net Tax Interest Expense for current period",
      "B",
    ],
    reactivations: ["Reactivations in the current period", "C"],
    current_yr_interest: [
      "Current Year Interest allowance used in current period",
      "D (B + C)",
    ],
    unused_interest_gen: [
      "Unused Interest Allowance generated in current period",
      "E (A - D)",
    ],
    unused_interest_bf: [
      "Unused Interest Allowance b/f used in current period",
      "",
    ],

    excess_debt_cap_from_prior_periodEDC: [
      "Excess debt cap generated in immediately preceding period",
      "A",
    ],
    angieorqngieEDC: ["ANGIE or QNGIE", "B"],
    group_debt_capEDC: ["Current period debt cap (s400(1)-(2))", "C = A + B"],
    group_debt_capEDC2: ["Current period debt cap (s400(1)-(2))", "C"],
    group_percentage_tax_ebitdaEDC: [
      "Less: 30% or Group Ratio % of Aggregate Tax-EBITDA",
      "D",
    ],
    excess_debt_cap_generated_pre_cflimitEDC: [
      "Excess debt cap generated in current period (s400(3)-(5)) before the application of the carry forward limit",
      "E = (C - D)",
    ],
    // excess_immediate: ["Excess debt cap generated in immediately preceding period","A"],
    group_disallowanceEDC: ["Disallowed amount for current period", "F"],
    cflimitEDC: ["Carry forward limit", "G = (A + F)"],
    group_excess_debt_capEDC: [
      "Excess debt cap generated in the period after the application of the carry-forward limit",
      "H = Lower of E and G",
    ],

    // Accounting Periods
    apCompNames: ["Company name"],
    apStartDates: ["Accounting Period Start Date"],
    apEndDates: ["Accounting Period End Date"],
    apCurrency: ["Currency"],
    apFx: ["Exchange rate (1 GBP = X Currency)"],

    APnltr_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    note_1: ["Less: Included in the above"],
    APnltr_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    APnltr_exclude_impairment_gains_losses: [
      "Exclude Impairment gains/(losses)",
    ],
    APnltr_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    APnltr_adjustment_amortised_cost_basis: [
      "Adjustment for election for amortised cost basis for fair valued creditor relationships",
    ],
    APnltr_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    APnltr_exclude_defined_benefit_pension: [
      "Exclude defined benefit pension income/expense included on loan relationships statement",
    ],
    APnltr_bank_expenses: [
      "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
    ],
    APnltr_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    APnltr_adjustment_lease_finance_cost: [
      "Adjustment for finance costs associated with leases",
    ],
    APnltr_other_adjustments_1: ["Other Adjustments 1"],
    APnltr_other_adjustments_2: ["Other Adjustments 2"],
    APnltr_other_adjustments_3: ["Other Adjustments 3"],
    APntlr_subtotal: ["Non-trading loan relationships - Subtotal"],

    APntdc_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    APntdc_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    APntdc_exclude_impairment_gains_losses: [
      "Exclude Impairment gains/(losses)",
    ],
    APntdc_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    APntdc_exclude_ordinary_trade: [
      "Exclude amounts on contracts arising in the ordinary course of trade",
    ],
    "APntdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)": [
      "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
    ],
    APntdc_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    APntdc_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    APntdc_adjustment_disregard_regulations: [
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    ],
    APntdc_other_adjustments_1: ["Other Adjustments 1"],
    APntdc_other_adjustments_2: ["Other Adjustments 2"],
    APntdc_other_adjustments_3: ["Other Adjustments 3"],
    APntdc_subtotal: ["Non-trading derivative contracts - subtotal"],

    APtlr_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    note_1: ["Less: Included in the above"],
    APtlr_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    APtlr_exclude_impairment_gains_losses: [
      "Exclude Impairment gains/(losses)",
    ],
    APtlr_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    APtlr_adjustment_amortised_cost_basis: [
      "Adjustment for election for amortised cost basis for fair valued creditor relationships",
    ],
    APtlr_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    APtlr_exclude_defined_benefit_pension: [
      "Exclude defined benefit pension income/expense included on loan relationships statement",
    ],
    APtlr_bank_expenses: [
      "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
    ],
    APtlr_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    APtlr_adjustment_lease_finance_cost: [
      "Adjustment for finance costs associated with leases",
    ],
    APtlr_other_adjustments_1: ["Other Adjustments 1"],
    APtlr_other_adjustments_2: ["Other Adjustments 2"],
    APtlr_other_adjustments_3: ["Other Adjustments 3"],
    APtlr_subtotal: ["Trading loan relationships - Subtotal"],

    APtdc_total_per_source_sheet_for_period: [
      "Total per Computation Schedule for period",
    ],
    APtdc_exclude_exchange_gains_losses: ["Exclude Exchange gains/(losses)"],
    APtdc_exclude_impairment_gains_losses: [
      "Exclude Impairment gains/(losses)",
    ],
    APtdc_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    APtdc_exclude_ordinary_trade: [
      "Exclude amounts on contracts arising in the ordinary course of trade",
    ],
    "APtdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)": [
      "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
    ],
    APtdc_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    APtdc_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    APtdc_adjustment_disregard_regulations: [
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    ],
    APtdc_other_adjustments_1: ["Other Adjustments 1"],
    APtdc_other_adjustments_2: ["Other Adjustments 2"],
    APtdc_other_adjustments_3: ["Other Adjustments 3"],
    APtdc_subtotal: ["Trading derivative contracts - subtotal"],

    APofa_total_per_source_sheet_for_period: [
      "Total Per Source Sheet For Period",
    ],
    APofa_exclude_tax_interest_income_double_relief: [
      "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    ],
    APofa_adjustment_qualifying_election: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    APofa_adjustment_gaap_transition: ["Adjustment for GAAP transition items"],
    APofa_adjustment_finance_lease: [
      "Adjustment to include Expense (income) associated with finance leases",
    ],
    APofa_adjustment_debt_factoring: [
      "Adjustment to include Expense (income) associated with debt factoring transactions",
    ],
    APofa_adjustment_service_concession: [
      "Adjustment to include Expense (income) associated with service concession arrangement if and to the extent accounted for as a financial liability",
    ],
    APofa_other_adjustments_1: ["Other Adjustments 1"],
    APofa_other_adjustments_2: ["Other Adjustments 2"],
    APofa_other_adjustments_3: ["Other Adjustments 3"],
    APofa_subtotal: ["Other financing arrangements - subtotal"],

    APnet_tax_interest_totals: ["Total net tax-interest expense/(income)"],
    tax_interest_total_for_period_gbp: [
      "Tax-Interest - Total for period (GBP)",
    ],
    net_tax_interest_expense_gbp: [
      "Total net tax-interest expense/(income) (GBP)",
    ],

    APcte_condition_a: [
      "Adjusted corporation tax earnings - Condition A (TIOPA 2010, s406(3))",
    ],
    APcte_condition_b: [
      "Adjusted corporation tax earnings - Condition B (TIOPA 2010, s406(4))",
    ],
    APcte_restrictions_reactivations_included: [
      "CIR (Restrictions) and reactivations included in the above",
    ],
    APtebitda_adjustments: ["Adjustments"],
    APcte_subtotal: [
      "Corporation tax earnings (excluding restrictions and reactivations)",
    ],

    APs407_tiopa_net_tax_interest_expense: [
      "Net tax-interest expense/(income) - s407(1)(a)",
    ],
    APs407_tiopa_capital_allowance_charges: [
      "Capital allowances and charges - s407(1)(b)",
    ],
    APs407_tiopa_relevant_intangible_debits_credits: [
      "Relevant intangible debits and credits - s407(1)(c)",
    ],
    APs407_tiopa_adjustment_intangible_debit_credits: [
      "Adjustment to intangible debits and credits brought in automatically (e.g. disposals above original cost)",
    ],
    APs407_tiopa_other_period_losses: [
      "Losses from other periods - s407(1)(d)(i) and (ii)",
    ],
    APs407_tiopa_loan_relationship_deficts_other_periods: [
      "Loan relationship deficits from other periods - s407(1)(e)",
    ],
    APs407_tiopa_expenses_management_other_periods: [
      "Expenses of management from other periods - s407(1)(f)",
    ],
    APs407_tiopa_group_relief: ["Group relief - s407(1)(g)"],

    APs407_relief_rd_expenditure_credit: [
      "R&D expenditure credit - s407(3)(a)",
    ],
    APs407_relief_rd_additional_relief: ["R&D additional relief - s407(3)(b)"],
    APs407_relief_trading_loss: [
      "Deemed trading loss for pre-trading expenditure - s407(3)(c)",
    ],
    APs407_relief_contaminated_land_relief: [
      "Contaminated land relief - s407(3)(d)",
    ],
    APs407_relief_creative_industries: ["Creative industries - s407(3)(e)-(j)"],
    APs407_relief_charitable_donation: [
      "Qualifying charitable donation (from current or earlier period) - s407(3)(k)",
    ],
    APs407_relief_patent_box_profits: [
      "Profits from patent box chargeable at a lower rate - s407(3)(l)",
    ],
    APs407_relief_dtr_adjustment_tax_rate_equal_uk: ["DTR adjustment - s409"],
    APs407_relief_adjustment_qualifying_infrastructure: [
      "Adjustment for election for Qualifying Infrastructure Company",
    ],
    APs407_relief_adjustment_gaap_items: [
      "Adjustment for GAAP transition items",
    ],
    APs407_relief_finance_lease_depreciation: [
      "Finance lease depreciation - s460(1)(d)",
    ],
    APs407_relief_reversal_of_lease_depreciation: [
      "Reversal of right-of-use lease depreciation in respect of operating leases",
    ],
    APs407_relief_long_funding_lease_deduction: [
      "Long funding lease deduction - s460(1)(a)",
    ],
    APs407_relief_finance_lease_rentals_receivable: [
      "Capital element of finance lease rentals receivable - s460(1)(c)",
    ],
    APs407_relief_long_funding_lessee: [
      "Long funding lessee - amount by which a deduction is reduced under section 379 of CTA 2010 - s460(1)(b)",
    ],
    APs407_relief_adjustment_misc_losses: [
      "Adjustment for miscellaneous losses not included in Condition B above",
    ],
    APs407_relief_group_relief: [
      "Group relief included above not covered by s407(1)(g)",
    ],
    APs407_relief_adjustment_disregarded_regulations: [
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    ],
    APs407_relief_other_adjustments_1: ["Other Adjustments 1"],
    APs407_relief_other_adjustments_2: ["Other Adjustments 2"],
    APs407_relief_other_adjustments_3: ["Other Adjustments 3"],
    APtax_ebitda: ["Tax-EBITDA - Total for period"],
    tax_ebitda_total_for_period_gbp: ["Tax-EBITDA - Total for period (GBP)"],

    APtax_ebitda_adjustments: [
      "Tax-EBITDA Adjustments (to apportionment calculation only)",
    ],

    // Group Summary and Companies
    subject_interest_restriction: [
      "Subject to interest restriction? Enter yes or no",
      interest_restrictions,
    ],
    total_dissallowed_amount: ["Total disallowed amount ", disallowed_amount],
    subject_reactivations: [
      "Subject to reactivations? Enter yes or no",
      reactivations,
    ],
    interest_reactivation_cap: ["Interest reactivation cap", reactivation_cap],
    aggregate_net_tax_interest_expense: [
      "Aggregate net tax-interest expense",
      aggr_ntie,
    ],
    aggregate_net_tax_interest_income: [
      "Aggregate net tax-interest expense",
      aggr_ntii,
    ],
    aggregate_tax_ebitda: ["Aggregate tax-EBITDA", aggr_ebitda],
    interest_allowance_brought_forward: [
      "Interest allowance brought forward",
      int_all_bfwd,
    ],
    interest_allowance_for_period: [
      "Interest allowance for period",
      int_all_period,
    ],
    interest_capacity_for_period: [
      "Interest capacity for period",
      int_capacity_period,
    ],
    adjusted_net_group_interest_expense: [
      "Adjusted net group interest expense",
      adj_ngie,
    ],

    qualifying_ngie: ["Qualifying net group-interest expense", qual_ngie],
    group_ebitda_summary: ["Group-EBITDA", group_ebitda],
    group_ratio_summary: ["Group ratio", group_ratio],
  };

  return excelData;
};

export const AP_potentialVariables = [
  "APnltr_total_per_source_sheet_for_period",
  "APnltr_exclude_exchange_gains_losses",
  "APnltr_exclude_impairment_gains_losses",
  "APnltr_exclude_tax_interest_income_double_relief",
  "APnltr_adjustment_amortised_cost_basis",
  "APnltr_adjustment_qualifying_election",
  "APnltr_exclude_defined_benefit_pension",
  "APnltr_bank_expenses",
  "APnltr_adjustment_gaap_transition",
  "APnltr_adjustment_lease_finance_cost",
  "APnltr_other_adjustments_1",
  "APnltr_other_adjustments_2",
  "APnltr_other_adjustments_3",
  "APntlr_subtotal",
  "APntdc_total_per_source_sheet_for_period",
  "APntdc_exclude_exchange_gains_losses",
  "APntdc_exclude_impairment_gains_losses",
  "APntdc_exclude_tax_interest_income_double_relief",
  "APntdc_exclude_ordinary_trade",
  "APntdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
  "APntdc_adjustment_qualifying_election",
  "APntdc_adjustment_gaap_transition",
  "APntdc_adjustment_disregard_regulations",
  "APntdc_other_adjustments_1",
  "APntdc_other_adjustments_2",
  "APntdc_other_adjustments_3",
  "APntdc_subtotal",
  "APtlr_total_per_source_sheet_for_period",
  "APtlr_exclude_exchange_gains_losses",
  "APtlr_exclude_impairment_gains_losses",
  "APtlr_exclude_tax_interest_income_double_relief",
  "APtlr_adjustment_amortised_cost_basis",
  "APtlr_adjustment_qualifying_election",
  "APtlr_exclude_defined_benefit_pension",
  "APtlr_bank_expenses",
  "APtlr_adjustment_gaap_transition",
  "APtlr_adjustment_lease_finance_cost",
  "APtlr_other_adjustments_1",
  "APtlr_other_adjustments_2",
  "APtlr_other_adjustments_3",
  "APtlr_subtotal",
  "APtdc_total_per_source_sheet_for_period",
  "APtdc_exclude_exchange_gains_losses",
  "APtdc_exclude_impairment_gains_losses",
  "APtdc_exclude_tax_interest_income_double_relief",
  "APtdc_exclude_ordinary_trade",
  "APtdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
  "APtdc_adjustment_qualifying_election",
  "APtdc_adjustment_gaap_transition",
  "APtdc_adjustment_disregard_regulations",
  "APtdc_other_adjustments_1",
  "APtdc_other_adjustments_2",
  "APtdc_other_adjustments_3",
  "APtdc_subtotal",
  "APofa_total_per_source_sheet_for_period",
  "APofa_exclude_tax_interest_income_double_relief",
  "APofa_adjustment_qualifying_election",
  "APofa_adjustment_gaap_transition",
  "APofa_adjustment_finance_lease",
  "APofa_adjustment_debt_factoring",
  "APofa_adjustment_service_concession",
  "APofa_other_adjustments_1",
  "APofa_other_adjustments_2",
  "APofa_other_adjustments_3",
  "APofa_subtotal",
  "APnet_tax_interest_totals",
  "APcte_condition_a",
  "APcte_condition_b",
  "APcte_restrictions_reactivations_included",
  "APtebitda_adjustments",
  "APcte_subtotal",
  "APs407_tiopa_net_tax_interest_expense",
  "APs407_tiopa_capital_allowance_charges",
  "APs407_tiopa_relevant_intangible_debits_credits",
  "APs407_tiopa_adjustment_intangible_debit_credits",
  "APs407_tiopa_other_period_losses",
  "APs407_tiopa_loan_relationship_deficts_other_periods",
  "APs407_tiopa_expenses_management_other_periods",
  "APs407_tiopa_group_relief",
  "APs407_relief_rd_expenditure_credit",
  "APs407_relief_rd_additional_relief",
  "APs407_relief_trading_loss",
  "APs407_relief_contaminated_land_relief",
  "APs407_relief_creative_industries",
  "APs407_relief_charitable_donation",
  "APs407_relief_patent_box_profits",
  "APs407_relief_dtr_adjustment_tax_rate_equal_uk",
  "APs407_relief_adjustment_qualifying_infrastructure",
  "APs407_relief_adjustment_gaap_items",
  "APs407_relief_finance_lease_depreciation",
  "APs407_relief_reversal_of_lease_depreciation",
  "APs407_relief_long_funding_lease_deduction",
  "APs407_relief_finance_lease_rentals_receivable",
  "APs407_relief_long_funding_lessee",
  "APs407_relief_adjustment_misc_losses",
  "APs407_relief_group_relief",
  "APs407_relief_adjustment_disregarded_regulations",
  "APs407_relief_other_adjustments_1",
  "APs407_relief_other_adjustments_2",
  "APs407_relief_other_adjustments_3",
  "APtax_ebitda",
  "tax_interest_total_for_period_gbp",
  "tax_ebitda_total_for_period_gbp",
];

export const ntlr_potentialVariables = [
  "nltr_total_per_source_sheet_for_period",
  "nltr_exclude_exchange_gains_losses",
  "nltr_exclude_impairment_gains_losses",
  "nltr_exclude_tax_interest_income_double_relief",
  "nltr_adjustment_amortised_cost_basis",
  "nltr_adjustment_qualifying_election",
  "nltr_exclude_defined_benefit_pension",
  "nltr_bank_expenses",
  "nltr_adjustment_gaap_transition",
  "nltr_adjustment_lease_finance_cost",
  "nltr_other_adjustments_1",
  "nltr_other_adjustments_2",
  "nltr_other_adjustments_3",
  "ntlr_subtotal",
];

export const tlr_potentialVariables = [
  "tlr_total_per_source_sheet_for_period",
  "tlr_exclude_exchange_gains_losses",
  "tlr_exclude_impairment_gains_losses",
  "tlr_exclude_tax_interest_income_double_relief",
  "tlr_adjustment_amortised_cost_basis",
  "tlr_adjustment_qualifying_election",
  "tlr_exclude_defined_benefit_pension",
  "tlr_bank_expenses",
  "tlr_adjustment_gaap_transition",
  "tlr_adjustment_lease_finance_cost",
  "tlr_other_adjustments_1",
  "tlr_other_adjustments_2",
  "tlr_other_adjustments_3",
  "tlr_subtotal",
];

export const ntdc_potentialVariables = [
  "ntdc_total_per_source_sheet_for_period",
  "ntdc_exclude_exchange_gains_losses",
  "ntdc_exclude_impairment_gains_losses",
  "ntdc_exclude_tax_interest_income_double_relief",
  "ntdc_exclude_ordinary_trade",
  "ntdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
  "ntdc_adjustment_qualifying_election",
  "ntdc_adjustment_gaap_transition",
  "ntdc_adjustment_disregard_regulations",
  "ntdc_other_adjustments_1",
  "ntdc_other_adjustments_2",
  "ntdc_other_adjustments_3",
  "ntdc_subtotal",
];

export const tdc_potentialVariables = [
  "tdc_total_per_source_sheet_for_period",
  "tdc_exclude_exchange_gains_losses",
  "tdc_exclude_impairment_gains_losses",
  "tdc_exclude_tax_interest_income_double_relief",
  "tdc_exclude_ordinary_trade",
  "tdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
  "tdc_adjustment_qualifying_election",
  "tdc_adjustment_gaap_transition",
  "tdc_adjustment_disregard_regulations",
  "tdc_other_adjustments_1",
  "tdc_other_adjustments_2",
  "tdc_other_adjustments_3",
  "tdc_subtotal",
];

export const ofa_potentialVariables = [
  "ofa_total_per_source_sheet_for_period",
  "ofa_exclude_tax_interest_income_double_relief",
  "ofa_adjustment_qualifying_election",
  "ofa_adjustment_gaap_transition",
  "ofa_adjustment_finance_lease",
  "ofa_adjustment_debt_factoring",
  "ofa_adjustment_service_concession",
  "ofa_other_adjustments_1",
  "ofa_other_adjustments_2",
  "ofa_other_adjustments_3",
  "ofa_subtotal",
  "net_tax_interest_expense_gbp",
];

export const cte_potentialVariables = [
  "cte_condition_a",
  "cte_condition_b",
  "cte_restrictions_reactivations_included",
  "tebitda_adjustments",
  "cte_subtotal",
];

export const adjustments_potentialVariables = [
  "s407_tiopa_net_tax_interest_expense",
  "net_tax_interest_totals",
  "s407_tiopa_capital_allowance_charges",
  "s407_tiopa_relevant_intangible_debits_credits",
  "s407_tiopa_adjustment_intangible_debit_credits",
  "s407_tiopa_other_period_losses",
  "s407_tiopa_loan_relationship_deficts_other_periods",
  "s407_tiopa_expenses_management_other_periods",
  "s407_tiopa_group_relief",
];

export const reliefs_potentialVariables = [
  "s407_relief_rd_expenditure_credit",
  "s407_relief_rd_additional_relief",
  "s407_relief_trading_loss",
  "s407_relief_contaminated_land_relief",
  "s407_relief_creative_industries",
  "s407_relief_charitable_donation",
  "s407_relief_patent_box_profits",
  "s407_relief_dtr_adjustment_tax_rate_equal_uk",
  "s407_relief_adjustment_qualifying_infrastructure",
  "s407_relief_adjustment_gaap_items",
  "s407_relief_finance_lease_depreciation",
  "s407_relief_reversal_of_lease_depreciation",
  "s407_relief_long_funding_lease_deduction",
  "s407_relief_finance_lease_rentals_receivable",
  "s407_relief_long_funding_lessee",
  "s407_relief_adjustment_misc_losses",
  "s407_relief_group_relief",
  "s407_relief_adjustment_disregarded_regulations",
  "s407_relief_other_adjustments_1",
  "s407_relief_other_adjustments_2",
  "s407_relief_other_adjustments_3",
  "tax_ebitda",
  "tax_ebitda_gbp",
];

export const dtr_potentialVariables = [
  "net_dtr_adjustment_gbp",
  "net_dtr_adjustment_lc",
  "corporation_tax_rate_percentage",
  "ct_rate_override",
  "s407_relief_dtr_adjustment_tax_rate_uk",
];

export const cir_potentialVariables = [
  "non_consenting_company",
  "trade_small_or_negligible",
  "trade_uncommercial_or_non_statutory",
  "investment_business_ceased",
  "cta10_change_in_ownership_rules",
];

export const company_potentialVariables = [
  "type_reit_company",
  "type_ring_fence_company",
  "type_investment_manager",
  "type_insurance_company",
  "type_shipping_company",
  "type_cooperative_company",
  "type_charity_company",
  "type_banking_company",
];

export const keyExceptionsMap = {
  s407_tiopa_net_tax_interest_expense: "net_tax_interest_expense",
  net_tax_interest_totals: "net_tax_interest_expense",

  // CIR Attributes
  current_int_allowance: "group_interest_allowance",
  current_tax_interest_expense: "aggregate_net_tax_interest_expense",
  reactivations: "group_reactivations",
  unused_interest_bf: "group_bf_interest_allowance",

  excess_debt_cap_from_prior_periodEDC: "excess_debt_cap_from_prior_period",
  group_debt_capEDC: "group_debt_cap",
  group_debt_capEDC2: "group_debt_cap",
  group_disallowanceEDC: "group_disallowance",
  group_excess_debt_capEDC: "group_excess_debt_cap",

  // CIR Calculations
  // fixed ratio variables
  aggregate_ebitda: "aggregate_tax_ebitda",
  angie: "adjusted_net_group_interest_expense",
  excess_debt: "excess_debt_cap_from_prior_period",
  fixed_debt: "fr_debt_cap",
  basic_interest: "fr_basic_interest_allowance",
  antii: "aggregate_net_tax_interest_income",
  interest_allowance: "fr_interest_allowance",

  // group ratio variable
  qngie: "qualifying_net_group_interest_expense",
  group_ratio: "gr_percentage",
  aggregate_ebitda2: "aggregate_tax_ebitda",
  group_debt_cap: "gr_debt_cap",
  group_basic_interest: "gr_basic_interest_allowance",
  group_interest_allowance: "gr_interest_allowance",
  excess_debt2: "excess_debt_cap_from_prior_period",

  // disallowed variables
  group_ratio_election: "apply_group_ratio_election",
  computation_int_allowance: "group_interest_allowance",
  unused_allowance: "group_unused_allowance",
  de_minimis: "interest_capacity_de_minimis",
  interest_capacity: "group_interest_capacity",
  aggregate_interest_expense: "aggregate_net_tax_interest_expense",
  disallowed_amount: "group_disallowance",
  available_reactivation: "group_interest_reactivation_cap",
};

export const calculationKeys = [
  "APnet_tax_interest_totals",
  "APs407_tiopa_net_tax_interest_expense",
  "current_yr_interest",
  "unused_interest_gen",
  "angieorqngieEDC",
  "group_percentage_tax_ebitdaEDC",
  "excess_debt_cap_generated_pre_cflimitEDC",
  "cflimitEDC",
  "fixed_ratio",
  "tax_interest_total_for_period_gbp",
  "tax_ebitda_total_for_period_gbp",
];

export const unused_variables = [
  "current_int_allowance",
  "current_tax_interest_expense",
  "reactivations",
  "current_yr_interest",
  "unused_interest_gen",
  "unused_interest_bf",
];

export const excess_debt_cap_variables = [
  "angieorqngieEDC",
  "excess_debt_cap_from_prior_periodEDC",
  "group_debt_capEDC",
  "group_debt_capEDC2",
  "group_percentage_tax_ebitdaEDC",
  "excess_debt_cap_generated_pre_cflimitEDC",
  "group_disallowanceEDC",
  "cflimitEDC",
  "group_excess_debt_capEDC",
  "excess_debt2",
];

export const fixed_ratio_variables = [
  "aggregate_ebitda",
  "fixed_ratio",
  "angie",
  "excess_debt",
  "fixed_debt",
  "basic_interest",
  "antii",
  "interest_allowance",
];

export const group_ratio_variables = [
  "qngie",
  "group_ebitda",
  "group_ratio",
  "aggregate_ebitda2",
  "group_ratio_ebitda",
  "group_debt_cap",
  "group_basic_interest",
  "group_interest_allowance",
];

export const disallowed_variables = [
  "group_ratio_election",
  "computation_int_allowance",
  "unused_allowance",
  "de_minimis",
  "interest_capacity",
  "aggregate_interest_expense",
  "disallowed_amount",
  "available_reactivation",
];

export const getAllocatedRestrictionsMetrics = () => {
  const { disallowed_amount } = getSummaryOfCalculationsData();

  return {
    aR_Row316: [
      "",
      "Total of allocated restrictions",
      ,
      "",
      "",
      "",
      "",
      "",
      "",
    ],
    aR_Row317: [
      "",
      "Total disallowed amount",
      ,
      "",
      "",
      "",
      "",
      "Copies from page 4",
      disallowed_amount,
    ],
  };
};

export const getAllocatedReactivationsMetrics = () => {
  const { reactivation_cap } = getSummaryOfCalculationsData();

  return {
    aReact_Row315: ["", "Total reactivation", "", ""],
    aReact_Row316: [
      "",
      "Reactivation cap",
      "",
      "Copies from page 4",
      reactivation_cap,
    ],
  };
};

const getFullReturnData = (savedData) => {
  const poaIndex = getPoaIndex();
  const poaData = savedData?.periods_of_account[poaIndex];

  const uniqueReturnCode = poaData?.return_status?.code;
  const returnTimestamp = poaData?.return_status?.timestamp;
  const natureOfRevision = poaData?.return_status?.nature_of_revision;
  const detailsOfEstimation = poaData?.return_status?.details_of_estimation;

  const gleC13 = poaData?.apply_group_ratio_election ? "E" : "";
  const gleC14 = poaData?.group_ratio_blended_election ? "E" : "";

  const groupEBITDAGains =
    savedData?.group_ebitda_gains_year &&
    processDateJS(poaData?.period_end)?.isSameOrAfter(
      processDateJS(savedData?.group_ebitda_gains_year),
    ) &&
    savedData?.group_ebitda_gains
      ? "E"
      : "";

  const interestAllowance_alternative =
    savedData?.interest_allowance_alt_calc_year &&
    processDateJS(poaData?.period_end).isSameOrAfter(
      processDateJS(savedData?.interest_allowance_alt_calc_year),
    ) &&
    savedData?.interest_allowance_alt_calc
      ? "E"
      : "";

  const interestAllowance_nonConsolidated =
    poaData?.election_interest_allowance_non_consolidated_investment ? "E" : "";

  const interestAllowance_consolidated =
    savedData?.interest_allowance_consolidated_partnership_year &&
    processDateJS(poaData?.period_end).isSameOrAfter(
      processDateJS(
        savedData?.interest_allowance_consolidated_partnership_year,
      ),
    ) &&
    savedData?.interest_allowance_consolidated_partnership
      ? "E"
      : "";

  // Build consolidated partnerships text for the return
  // Only show if election is active for this period (same check as interestAllowance_consolidated marker)
  let consolidatedPartnershipsText = "";
  const electionActiveForPeriod =
    savedData?.interest_allowance_consolidated_partnership_year &&
    processDateJS(poaData?.period_end).isSameOrAfter(
      processDateJS(
        savedData?.interest_allowance_consolidated_partnership_year,
      ),
    ) &&
    savedData?.interest_allowance_consolidated_partnership;

  if (electionActiveForPeriod) {
    // Normalize legacy data formats before filtering
    const normalizedPartnerships = normalizeConsolidatedPartnerships(
      savedData?.consolidated_partnerships || [],
    );

    const validPartnerships = normalizedPartnerships.filter((partnership) => {
      const partnershipDate = processDateJS(partnership.date);
      const poaEndDate = processDateJS(poaData?.period_end);
      const disposalDate = partnership.disposedDate
        ? processDateJS(partnership.disposedDate)
        : null;

      // Include if partnership date is on or before PoA end date
      // AND either no disposal date OR disposal date is after PoA end date
      if (
        partnershipDate &&
        poaEndDate &&
        partnershipDate.isSameOrBefore(poaEndDate, "day")
      ) {
        return !disposalDate || disposalDate.isAfter(poaEndDate, "day");
      }
      return false;
    });

    if (validPartnerships.length > 0) {
      // Use rich text so only the labels render bold in the Excel cell.
      const consolidatedPartnershipsRichText = [];

      validPartnerships.forEach((p, index) => {
        if (index > 0) {
          consolidatedPartnershipsRichText.push({ text: "\n" });
        }

        consolidatedPartnershipsRichText.push(
          { text: "Name:", font: { bold: true } },
          { text: ` ${p.name}` },
          { text: " - " },
          { text: "UTR: ", font: { bold: true } },
          { text: p.utr || "N/A" },
        );
      });

      consolidatedPartnershipsText = {
        richText: consolidatedPartnershipsRichText,
      };
    } else {
      // If election is true but no valid partnerships, show disposal message
      consolidatedPartnershipsText =
        "The group has previously made a Consolidated Partnerships election, but all of the partnerships in respect of which an election has been made were disposed of before the start of the period";
    }
  }

  const arrayOfInvestorGroups = poaData?.investor_group_data;
  let investorGroupNames = [];
  if (arrayOfInvestorGroups) {
    arrayOfInvestorGroups.forEach((group) => {
      investorGroupNames.push(group.investor_group);
    });
    investorGroupNames = investorGroupNames.join(", ");
  }

  const ultimateParent = savedData.companies.find(
    (comp) => comp.company_ID === savedData.ult_parent_ID,
  );
  const ultimate_parent_name = ultimateParent?.company_name;
  const ultimate_parent_utr = ultimateParent?.utr;
  const ultimate_parent_lei = savedData?.ult_parent_lei
    ? savedData?.ult_parent_lei
    : "N/A";

  const reporting_company = savedData.reporting_company.find(
    (comp) => comp.company_ID === poaData?.reporting_companyID,
  );
  const reporting_company_name = reporting_company?.company_name;
  const reporting_company_utr = reporting_company?.utr;
  const revised_return = poaData.return_status?.isRevised ? "Yes" : "No";

  const poaStart = processDateJS(poaData?.period_start).format("DD/MM/YYYY");
  const poaEnd = processDateJS(poaData?.period_end).format("DD/MM/YYYY");

  return {
    uniqueReturnCode,
    returnTimestamp,
    natureOfRevision,
    detailsOfEstimation,
    gleC13,
    gleC14,
    groupEBITDAGains,
    interestAllowance_alternative,
    interestAllowance_nonConsolidated,
    interestAllowance_consolidated,
    consolidatedPartnershipsText,
    investorGroupNames,
    ultimate_parent_name,
    ultimate_parent_utr,
    ultimate_parent_lei,
    reporting_company,
    reporting_company_name,
    reporting_company_utr,
    revised_return,
    poaStart,
    poaEnd,
  };
};

export const getFullReturnExcelData = () => {
  const poaIndex = getPoaIndex();
  const savedData = getGroupData();
  const poaData = savedData?.periods_of_account[poaIndex];

  const {
    interest_restrictions,
    disallowed_amount,
    reactivation_cap,
    reactivations,
    aggr_ntie,
    aggr_ntii,
    aggr_ebitda,
    int_all_bfwd,
    int_all_period,
    int_capacity_period,
    adj_ngie,
    qual_ngie,
    group_ebitda,
    group_ratio,
    investorsList,
  } = getSummaryOfCalculationsData();

  const {
    uniqueReturnCode,
    returnTimestamp,
    natureOfRevision,
    detailsOfEstimation,
    gleC13,
    gleC14,
    groupEBITDAGains,
    interestAllowance_alternative,
    interestAllowance_nonConsolidated,
    interestAllowance_consolidated,
    consolidatedPartnershipsText,
    investorGroupNames,
    ultimate_parent_name,
    ultimate_parent_utr,
    ultimate_parent_lei,
    reporting_company_name,
    reporting_company_utr,
    revised_return,
    poaStart,
    poaEnd,
  } = getFullReturnData(savedData);

  const excelData = {
    // Group Period Return Type tab values
    start_period_account: ["", "Start of period of account", "", poaStart],
    end_period_account: ["", "End of period of account", "", poaEnd],
    reporting_company: ["", "Reporting company", "Name"],
    rep_comp_name: ["", "Reporting company", "Name", reporting_company_name],
    rep_comp_UTR: ["", " ", "UTR (10 Digits)", reporting_company_utr],
    ultimate_parent: ["", "Ultimate parent", "Name", ultimate_parent_name],
    ult_parent_line2: ["", "", "UTR (if any, 10 digits)", ultimate_parent_utr],
    ult_parent_line3: [
      "",
      "",
      "Jurisdiction of incorporation",
      migrateUlt_parent_incorporation_jurisdiction(savedData)
        ?.ult_parent_incorporation_jurisdiction,
    ],
    ult_parent_line4: [
      "",
      "",
      "Legal Entity Identifier (LEI)",
      ultimate_parent_lei,
    ],
    ult_parent_optional: [
      "",
      "(Optional, where not name of ultimate parent)",
      "Name by which group is to be known",
      "",
    ],
    abbreviated_election: ["", "Abbreviated return election?", "No"],
    revised_return: ["", "Revised return", revised_return],
    subject_to_restrictions: [
      "",
      "Group subject to interest restrictions?",
      interest_restrictions,
    ],
    subject_to_reactivations: [
      "",
      "Group subject to interest reactivations?",
      reactivations,
    ],
    nature_of_revision: ["", `${natureOfRevision ?? ""}`],
    details_of_estimation: ["", `${detailsOfEstimation ?? ""}`],
    return_details: [
      "",
      `Unique return code: ${uniqueReturnCode}`,
      `Timestamp: ${returnTimestamp} (GMT)`,
    ],

    // Group Level Elections tab values
    gleRepComp: ["", "Name of reporting company", reporting_company_name],
    gleUltParent: [
      "",
      "Ultimate parent or name of group",
      ultimate_parent_name,
    ],
    gleEndPoA: ["", "End of period of account", poaEnd],
    gleRow13: ["", "Group ratio election (enter here only)", gleC13],
    gleRow14: ["", "Group ratio (blended) election (see below)", gleC14],
    gleRow15: [
      "",
      "Group EBITDA (chargeable gains) election",
      groupEBITDAGains,
      "irrevocable",
    ],
    gleRow16: [
      "",
      "Interest allowance (alternative calculation) election",
      interestAllowance_alternative,
      "irrevocable",
    ],
    gleRow17: [
      "",
      "Interest allowance (non-consolidated investment) election (see below)",
      interestAllowance_nonConsolidated,
    ],
    gleRow18: [
      "",
      "Interest allowance (consolidated partnerships) election (see below)",
      interestAllowance_consolidated,
      "irrevocable",
    ],
    gleRow23: ["", investorsList],
    gleRow27: ["", ""],
    gleRow31: ["", consolidatedPartnershipsText],

    // UK Group Companies tab values
    ukGC_Row7: [
      "",
      "Name of reporting company",
      "",
      reporting_company_name,
      "",
      "",
    ],
    ukGC_Row8: ["", "Ultimate parent ", "", ultimate_parent_name, "", ""],
    ukGC_Row9: ["", "Name used for group, if applicable"],
    ukGC_Row10: [
      "",
      "End of period of account",
      "",
      processDateJS(savedData.periods_of_account[poaIndex]?.period_end).format(
        "DD/MM/YYYY",
      ),
      "",
      "",
    ],

    // Group level amounts values
    gla_Row7: ["", "Name reporting company", reporting_company_name],
    gla_Row8: ["", "Ultimate parent", ultimate_parent_name],
    gla_Row9: ["", "Name used for group, if applicable"],
    gla_Row10: [
      "",
      "End of period of account",
      processDateJS(savedData.periods_of_account[poaIndex]?.period_end).format(
        "DD/MM/YYYY",
      ),
    ],
    gla_Row17: [
      "",
      "Subject to interest restriction? Enter yes or no",
      interest_restrictions,
    ],
    gla_Row18: ["", "Total disallowed amount ", disallowed_amount],
    gla_Row19: ["", "Subject to reactivations? Enter yes or no", reactivations],
    gla_Row20: ["", "Interest reactivation cap ", reactivation_cap],
    gla_Row22: ["", "Aggregate net tax-interest expense", aggr_ntie],
    gla_Row23: ["", "Aggregate net tax-interest income", aggr_ntii],
    gla_Row24: ["", "Aggregate tax-EBITDA", aggr_ebitda],
    gla_Row25: ["", "Interest allowance brought forward", int_all_bfwd],
    gla_Row26: ["", "Interest allowance for period", int_all_period],
    gla_Row27: ["", "Interest capacity for period", int_capacity_period],
    gla_Row29: ["", "Adjusted net group interest expense ", adj_ngie],
    gla_Row31: ["", "Only where group ratio election made", ""],
    gla_Row32: ["", "Qualifying net group-interest expense", qual_ngie],
    gla_Row33: ["", "Group-EBITDA", group_ebitda],
    gla_Row34: ["", "Group ratio", group_ratio],

    ...getAllocatedRestrictionsMetrics(),
    ...getAllocatedReactivationsMetrics(),
  };

  return excelData;
};
